import React, { JSX } from "react";
import styles from "../assets/style.module.css";
import { ApiResponseData, BaseService, ITeamMember, ITeamNews, ITeamWebsite, OmneHostService, SanitizedResponse } from "../../../../services";
import { Classname, Paginator, Scheme, useSearchParams } from "@rivtn/noseur";
import { Utils } from "../../../../utils";

interface NewsDetailProps { article: ITeamNews; onBack: () => void; }

export function NewsDetail({ article, onBack }: NewsDetailProps): JSX.Element {
    return (
        <div className={styles["section"]}>
            <button className={styles["back-btn"]} onClick={onBack}>← Back to News</button>

            <div className={styles["news-detail"]}>
                {(!!article.extra_data.category) && (<div className={styles["news-cat"]} style={{ marginBottom: "0.75rem" }}>{article.extra_data.category}</div>)}
                <div className={styles["news-detail-title"]}>{article.title}</div>
                <div className={styles["news-meta"]} style={{ marginBottom: "1.5rem" }}><span>📅 {Utils.fineDate(new Date(article.updated_at))}</span></div>
                {(!!article.extra_data?.featured_image) && (<img className={styles["news-detail-img"]} src={article.extra_data?.featured_image} alt={article.title} />)}

                <div className={styles["news-detail-body"]} dangerouslySetInnerHTML={{ __html: Utils.markdownToHtml(article.content) }} />
            </div>
        </div>
    );
}

interface NewsProps {
    website: ITeamWebsite;
}

export function News(props: NewsProps): JSX.Element {

    const omnehostService = OmneHostService.getInstance();
    const [filter, setFilter] = React.useState<string>("All");
    const [cats, setCats] = React.useState<Set<string>>(new Set());
    const [teamNews, setTeamNews] = React.useState<ApiResponseData<ITeamNews>>();
    const [selectedNews, setSelectedNews] = React.useState<ITeamNews | null>(null);
    const { page, setPage, article, setArticle } = useSearchParams({ defaultInit: window.location.search, params: { page: "1", article: "" } });

    React.useEffect(fetchInformation, [omnehostService, page]);
    React.useEffect(() => {
        if (!article) {
            setSelectedNews(null);
        }
    }, [article]);

    if (selectedNews) {
        return (<NewsDetail article={selectedNews} onBack={() => {
            setSelectedNews(null);
            setArticle(undefined);
        }} />);
    }

    return (<>
        <div className={styles["section"]}>
            <div className={styles["sec-line"]} />
            <h1 className={styles["sec-title"]}>Team <em>News</em></h1>
            <p className={styles["sec-sub"]}>Latest updates, match reports and announcements</p>

            <div className={styles["tabs"]}>
                {Array.from(cats).map((c: string) => (
                    <button
                        key={c}
                        className={`${styles["tab-btn"]} ${filter === c ? styles["active"] : ""}`}
                        onClick={() => setFilter(c)}
                    >
                        {c}
                    </button>
                ))}
            </div>

            {(!!teamNews?.content)
                ? ((teamNews.content && teamNews.content?.length > 0)
                    ? (<div className={styles["news-grid"]}>
                        {teamNews.content!.filter((g) => {
                            return true;//(!galleryFilter || g.extra_data?.tags?.includes(galleryFilter));
                        }).map((n) => (<div key={n.external_id} className={styles["news-card"]} onClick={() => {
                            setArticle(n.external_id);
                            setSelectedNews(n);
                        }}>
                            {(!!n.extra_data?.featured_image) && (<img className={styles["news-card-img"]} src={n.extra_data.featured_image} alt={n.title} loading="lazy" />)}
                            <div className={styles["news-card-body"]}>
                                {(!!n.extra_data?.category) && (<div className={styles["news-cat"]}>{n.extra_data.category}</div>)}
                                <div className={styles["news-title"]}>{n.title}</div>
                                <div className={styles["news-excerpt"]}>{n.content.substring(0, 120)}</div>
                                <div className={styles["news-meta"]}><span>📅 {Utils.fineDate(new Date(n.updated_at))}</span></div>
                            </div>
                        </div>))}
                    </div>)
                    : (Utils.emptyState()))
                : (Utils.loadingState())}

            <Paginator scheme={Scheme.LIGHT} style={{ marginTop: 50 }} rowsPerPage={teamNews?.size} totalRecords={teamNews?.total_elements} initialPage={parseInt("" + page)} onPageChange={(e) => {
                setPage("" + e.currentPage)
            }} template={{
                activePageLabel: () => "",
                nextPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                previousPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                lastPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                firstPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                pageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, (option.active ? styles["active"] : null))} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick}>{option.label}</button>),
            }} />
        </div>
    </>);

    function fetchInformation() {
        setTeamNews({});
        omnehostService.queryTeamNews(props.website.team.public_id, {
            page,
            filter,
            sort: "updated_at,DESC",
        }).then(({ apiResponse, content }: SanitizedResponse<ITeamNews>) => {
            setTeamNews(apiResponse.data!);
            setCats(new Set(content?.reduce((acc, v) => {
                if (article && article === v.external_id) { // ;)
                    setSelectedNews(v);
                }
                return acc.concat(v.extra_data?.category ? [v.extra_data?.category] : []);
            }, [] as string[]) ?? []));
        }).catch(BaseService.reportError);
    }

}

export default News;